import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProductionResourcesToolsView = ({ formData, setFormData, selectedPlants = [], readOnly = false }) => {
  const [taskListUsages, setTaskListUsages] = useState([]);
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);

  const productionData = formData.production_resources_tools || {
    plant_id: '',
    task_list_usage_id: '',
    refer_date_start: '',
    refer_date_finish: ''
  };

  useEffect(() => {
    loadMasterData();
  }, []);

  const loadMasterData = async () => {
    try {
      const [
        taskListUsagesRes,
        plantsRes
      ] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/task-list-usages`),
        axios.get(`${BACKEND_URL}/api/material-management/plants`)
      ]);

      setTaskListUsages(taskListUsagesRes.data || []);
      setPlants(plantsRes.data || []);
    } catch (error) {
      console.error('Error loading master data:', error);
      toast.error('Failed to load master data');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      production_resources_tools: {
        ...prev.production_resources_tools,
        [field]: value
      }
    }));
  };

  const getAvailablePlants = () => {
    if (selectedPlants.length === 0) {
      return plants;
    }
    return plants.filter(p => selectedPlants.includes(p.plant_id));
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-10 h-10 animate-spin text-orange-600 mb-3" />
        <p className="text-sm text-gray-600">Loading production resources master data...</p>
      </div>
    );
  }

  const availablePlants = getAvailablePlants();
  const plantConsistencyWarning = selectedPlants.length > 0;

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-3">
        <h3 className="text-lg font-semibold text-gray-900">Production Resources / Tools</h3>
        <p className="text-sm text-gray-600 mt-1">
          Configure production planning and task list information
        </p>
      </div>

      {/* Plant Consistency Warning */}
      {plantConsistencyWarning && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-orange-900">Plant Consistency Enforced</h4>
            <p className="text-sm text-orange-700 mt-1">
              Plant selection is restricted to plants already selected in other views ({selectedPlants.join(', ')}).
            </p>
          </div>
        </div>
      )}

      {/* Plant Selection */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-orange-900 mb-3">Plant *</h4>
        <div>
          <Label>Plant *</Label>
          <select
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            value={productionData.plant_id}
            onChange={(e) => handleFieldChange('plant_id', e.target.value)}
          >
            <option value="">Select Plant</option>
            {availablePlants.map(p => (
              <option key={p.plant_id} value={p.plant_id}>
                {p.plant_id} - {p.plant_name}
              </option>
            ))}
          </select>
          {availablePlants.length === 0 && selectedPlants.length > 0 && (
            <p className="text-xs text-red-600 mt-1">No valid plants available. Please check plant selection in other views.</p>
          )}
        </div>
      </div>

      {/* Task List Configuration */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Task List Configuration</h4>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <Label>Task List Usage</Label>
            <select
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={productionData.task_list_usage_id}
              onChange={(e) => handleFieldChange('task_list_usage_id', e.target.value)}
            >
              <option value="">Select Task List Usage (Optional)</option>
              {taskListUsages.map(tlu => (
                <option key={tlu.usage} value={tlu.usage}>
                  {tlu.usage} - {tlu.description}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Related to Task Lists/Routings for production</p>
          </div>
        </div>
      </div>

      {/* Reference Dates */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Reference Dates for Scheduling</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Reference Date for Start Date</Label>
            <Input
              type="date"
              value={productionData.refer_date_start}
              onChange={(e) => handleFieldChange('refer_date_start', e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-1">Used in production/order scheduling</p>
          </div>
          <div>
            <Label>Reference Date for Finish Date</Label>
            <Input
              type="date"
              value={productionData.refer_date_finish}
              onChange={(e) => handleFieldChange('refer_date_finish', e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-1">Used in production/order scheduling</p>
          </div>
        </div>
      </div>

      {/* Info Note */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
        <p className="font-medium text-gray-900 mb-1">📋 Note:</p>
        <p>
          Fields marked with * are mandatory. This view configures production-related parameters including
          task lists and reference dates used for production order scheduling. Task list usage is often
          derived from the material type configuration.
        </p>
      </div>
    </div>
  );
};

export default ProductionResourcesToolsView;
